import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Alert } from '@mui/material';
import { apiRequest } from '../../../utils/fetch';
import { AdSpacePaymentDetailsInterface, GenericResponseInterface } from '../../../interfaces/interfaces';

const initialPayment: Partial<AdSpacePaymentDetailsInterface> = {
  userId: 0,
  status: 'pending',
  stripeAmountPaid: 0,
};

const PaymentDetailsAddPage: React.FC = () => {
  const [payment, setPayment] = useState<Partial<AdSpacePaymentDetailsInterface>>(initialPayment);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleChange = (field: keyof AdSpacePaymentDetailsInterface, value: any) => {
    setPayment(p => ({ ...p, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate required fields
    if (!payment.userId) {
      setError('User ID is required');
      return;
    }

    try {
      const response = await apiRequest<GenericResponseInterface<AdSpacePaymentDetailsInterface>>(
        '/paymentDetails/insert',
        'POST',
        payment
      );
      
      if (response.status) {
        setSuccess(response.message || 'Payment details created successfully!');
        setPayment(initialPayment);
      } else {
        setError(response.message || 'Failed to create payment details');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create payment details');
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>Add New Payment Record</Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        This should only be used for manual payment entry in exceptional cases.
        Normal payments are automatically created through Stripe.
      </Alert>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      
      <form onSubmit={handleSubmit}>
        <TextField
          label="User ID"
          type="number"
          value={payment.userId ?? ''}
          onChange={e => handleChange('userId', Number(e.target.value))}
          fullWidth
          margin="dense"
          required
        />
        
        <TextField
          label="Stripe Payment Intent ID"
          value={payment.stripePaymentIntentId ?? ''}
          onChange={e => handleChange('stripePaymentIntentId', e.target.value)}
          fullWidth
          margin="dense"
          helperText="Optional - Stripe payment intent ID if this is linked to Stripe"
        />
        
        <TextField
          label="Stripe Session ID"
          value={payment.stripeSessionId ?? ''}
          onChange={e => handleChange('stripeSessionId', e.target.value)}
          fullWidth
          margin="dense"
          helperText="Optional - Stripe session ID"
        />
        
        <TextField
          label="Amount Paid (in cents)"
          type="number"
          value={payment.stripeAmountPaid ?? ''}
          onChange={e => handleChange('stripeAmountPaid', Number(e.target.value))}
          fullWidth
          margin="dense"
          required
          helperText="Amount in cents (e.g., 10000 = $100.00)"
        />
        
        <FormControl fullWidth margin="dense">
          <InputLabel id="status-label">Status</InputLabel>
          <Select
            labelId="status-label"
            value={payment.status ?? 'pending'}
            label="Status"
            onChange={e => handleChange('status', e.target.value)}
            required
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FormControl>
        
        <TextField
          label="Completed At"
          type="datetime-local"
          value={payment.completedAt ? new Date(payment.completedAt).toISOString().slice(0, 16) : ''}
          onChange={e => handleChange('completedAt', new Date(e.target.value))}
          fullWidth
          margin="dense"
          InputLabelProps={{ shrink: true }}
          helperText="Optional - When the payment was completed"
        />
        
        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            fullWidth
          >
            Create Payment Record
          </Button>
          <Button 
            type="button" 
            variant="outlined"
            onClick={() => setPayment(initialPayment)}
            fullWidth
          >
            Reset
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default PaymentDetailsAddPage;
